import * as T from '@radix-ui/react-toast';
import { type ReactNode, useCallback, useRef, useState } from 'react';

import { tokenMs } from '../app/motion.ts';
import { escapeToLayerBelow } from './layers.ts';
import './ui.css';

/** Where the slip sits: over the clue band in a game, or above the tab bar on a page. */
export type ToastPlace = 'over-band' | 'above-tabbar';

const toastMs = () => tokenMs('--dur-toast', 2400);

export interface ToastProps {
	text: string;
	place: ToastPlace;
	/** Raised by a sheet: it sits over that sheet. */
	overSheet?: boolean | undefined;
	testId?: string | undefined;
	/** Controlled; leave it out and the slip shows once, for --dur-toast (give it a new `key` to show it again). */
	open?: boolean | undefined;
	onOpenChange?: ((open: boolean) => void) | undefined;
	/** A bold first line over `text` (a toast with an action). */
	title?: string | undefined;
	/**
	 * Something to press ("Turn on"): the toast is then a real, reachable control (Radix
	 * Toast.Action), not the eye-only slip. Pressing it runs `onAction` and closes the toast;
	 * `altText` is what a screen reader hears for it in the announcement.
	 */
	action?: ToastAction | undefined;
	/** How long it stays (ms); hovering or focusing it holds it. Default --dur-toast. */
	duration?: number | undefined;
}

export interface ToastAction {
	label: string;
	altText: string;
	onAction: () => void;
}

/**
 * The slip (Radix Toast; ui.css `.slip-toast`): ink on a vermilion lip, there for --dur-toast,
 * over the clue band in a game or above the tab bar on a page. Radix keeps its time and takes
 * it away. The slip is for the eye only: it's inert and aria-hidden, and Radix's own announcement
 * is sent nowhere, because the words reach a screen reader through a polite live
 * region (`useToast`'s, or the caller's own, as the game's), or a sheet's `SheetSay` while a
 * sheet is up, where an announcement from the inert page would never be heard (QA4-2).
 *
 * With an `action` it's a card with a button (ui.css `.slip-act`, the nudge moment's "Turn
 * on"): reachable (F8, or Tab once it's up), announced politely by Radix itself (its words and
 * the action's `altText`), held while it has the pointer or focus, and Escape closes it.
 */
export function Toast({ text, place, overSheet = false, testId, open, onOpenChange, title, action, duration }: ToastProps) {
	const control = open === undefined ? { defaultOpen: true } : { open, onOpenChange };
	// Radix's own announcement goes into a fragment that's never in the page (see above).
	const [unsaid] = useState(() => (typeof document === 'undefined' ? undefined : document.createDocumentFragment()));
	const live = !!action;
	return (
		<div className="ui-toast-host" inert={!live}>
			<T.Provider label="Notification" duration={duration ?? toastMs()} swipeDirection="down" announcerContainer={live ? undefined : unsaid}>
				<T.Root
					{...control}
					type="background"
					className={live ? 'slip slip-toast slip-act' : 'slip slip-toast'}
					data-place={place}
					data-over={overSheet ? 'sheet' : undefined}
					data-testid={testId}
					aria-hidden={live ? undefined : 'true'}
					tabIndex={live ? undefined : -1}
					// A slip that's up is Radix's newest layer: Escape still belongs to the sheet or popover
					// under it. A toast with an action is something to answer: Escape closes it.
					onEscapeKeyDown={(e) => {
						if (live || !escapeToLayerBelow()) return;
						e.preventDefault();
						e.stopPropagation();
					}}
				>
					{live ? (
						<>
							<T.Description className="slip-act-t">
								{title && <b>{title}</b>} {text}
							</T.Description>
							<T.Action className="slip-act-b" altText={action.altText} onClick={action.onAction}>
								{action.label}
							</T.Action>
						</>
					) : (
						text
					)}
				</T.Root>
				<T.Viewport className="ui-toast-vp" />
			</T.Provider>
		</div>
	);
}

/**
 * A sheet's own polite live region (ui/Sheet renders one in every sheet). While a sheet is
 * up the page behind it is inert, and a screen reader hears nothing from an inert live
 * region, so a toast raised then ("Link copied" in the invite sheet) is said from here
 * (QA4-2). A live region without the status role, so a sheet's own status lines stay the
 * only `role=status` in it. Empty until a toast writes to it.
 */
export function SheetSay() {
	return <div className="sr-only" aria-live="polite" data-sheet-say="" />;
}

/** The open sheet on top, if any: its `SheetSay`. */
function sheetRegion(): HTMLElement | null {
	if (typeof document === 'undefined') return null;
	const all = document.querySelectorAll<HTMLElement>('[data-sheet-say]');
	return all[all.length - 1] ?? null;
}

/** Says `text` from the open sheet's region: a fresh line each time, so the same words are read again. */
function sayInSheet(region: HTMLElement, text: string) {
	const line = document.createElement('p');
	line.textContent = text;
	region.replaceChildren(line);
}

/**
 * A toast for a page ("Link copied", "Nudge sent"): `show(text)` puts up the slip and says
 * the words once, politely. The live region is always in the page, so the first message is
 * read too; while a sheet is open the words are said from the sheet instead (`SheetSay`).
 * Render `node` once, anywhere in the page.
 */
export function useToast({ place = 'above-tabbar', overSheet = false, testId }: { place?: ToastPlace; overSheet?: boolean; testId?: string } = {}): {
	show: (text: string) => void;
	node: ReactNode;
} {
	const [toast, setToast] = useState<{
		text: string;
		serial: number;
		inSheet: boolean;
	} | null>(null);
	const region = useRef<HTMLElement | null>(null);
	const show = useCallback((text: string) => {
		const r = sheetRegion();
		if (r) sayInSheet(r, text);
		region.current = r;
		setToast((cur) => ({
			text,
			serial: (cur?.serial ?? 0) + 1,
			inSheet: r !== null,
		}));
	}, []);
	const gone = (text: string) => {
		setToast(null);
		const r = region.current;
		if (r?.isConnected && r.textContent === text) r.replaceChildren();
	};
	const node = (
		<>
			<div className="sr-only" role="status" aria-live="polite">
				{toast && !toast.inSheet ? toast.text : ''}
			</div>
			{toast && (
				<Toast
					key={toast.serial}
					text={toast.text}
					place={place}
					overSheet={overSheet}
					testId={testId}
					open
					onOpenChange={(o) => {
						if (!o) gone(toast.text);
					}}
				/>
			)}
		</>
	);
	return { show, node };
}
